// errmon.ts — Bugsnag error monitoring (2026-08, upload-failure debugging).
//
// The notifier API key is a public client key (it can only submit events, not
// read them), so it lives in the bundle like the Supabase anon key does.
// Events are sent ONLY from the deployed stages — local dev (localhost /
// unstamped builds) initializes Bugsnag but never reports.
//
// Wiring: main.ts's log() feeds every log line in as a breadcrumb, so an
// upload-failure event arrives with the whole session narrative (connect,
// pull, hash, per-file progress, retries) attached. upload.ts breadcrumbs
// each retry attempt individually — the final error only carries the last
// failure, but the retries tell you whether the link flaked 4 times first.
import Bugsnag from "@bugsnag/js";

const BUGSNAG_API_KEY = "abc24f60d08bc0e98089deffff5918d5";

const stage =
  location.hostname === "app.egocentric.com" ? "production"
  : location.hostname.endsWith(".pages.dev") ? "staging"
  : "development";

// The footer build stamp is the deploy identity ("<sha>[+] · <utc minute>",
// stamped by assemble.mjs) — the same string used to answer "did my deploy
// land?", so Bugsnag events correlate 1:1 with it.
const appVersion =
  document.getElementById("buildStamp")?.textContent?.replace(/^web\s*/, "") ?? "unknown";

const enabled = /^[0-9a-f]{32}$/.test(BUGSNAG_API_KEY);
if (enabled) {
  // Debug seam: the SDK is bundled (not on window like the CDN snippet), so
  // expose it for DevTools smoke tests — Bugsnag.notify(new Error("test")).
  (window as any).Bugsnag = Bugsnag;
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    releaseStage: stage,
    enabledReleaseStages: ["production", "staging"],
    appVersion,
    onError: async (event) => {
      // Network state at the moment of the event — the first question for any
      // upload failure. navigator.connection is Chromium-only; Portal is
      // Chromium-only anyway (WebUSB).
      const c = (navigator as any).connection;
      event.addMetadata("network", {
        onLine: navigator.onLine,
        effectiveType: c?.effectiveType ?? null,
        downlinkMbps: c?.downlink ?? null,
        rttMs: c?.rtt ?? null,
      });
      // Storage headroom — big sessions stage into OPFS, so quota exhaustion
      // is a prime suspect whenever "big file fails, small file works".
      try {
        const { usage, quota } = await navigator.storage.estimate();
        event.addMetadata("storage", {
          usageGB: usage != null ? +(usage / 2 ** 30).toFixed(2) : null,
          quotaGB: quota != null ? +(quota / 2 ** 30).toFixed(2) : null,
          freeGB: usage != null && quota != null ? +((quota - usage) / 2 ** 30).toFixed(2) : null,
        });
      } catch { /* estimate unsupported */ }
    },
  });
}

/** Attach the signed-in user to all subsequent events. */
export function errmonUser(id: string | null, email?: string | null): void {
  if (enabled) Bugsnag.setUser(id ?? undefined, email ?? undefined);
}

/** Manual breadcrumb (log() lines route through here). */
export function breadcrumb(message: string, metadata?: Record<string, unknown>): void {
  if (enabled) Bugsnag.leaveBreadcrumb(message.slice(0, 500), metadata, "log");
}

/** Report a handled error with a named context (e.g. "upload") + metadata.
 *  Errors thrown via upload.ts's fail() carry an `.info` object (phase, HTTP
 *  status, part number, …) — merged into the event automatically. */
export function reportError(
  err: unknown, context: string, metadata?: Record<string, unknown>,
): void {
  if (!enabled) return;
  const info = (err as any)?.info as Record<string, unknown> | undefined;
  Bugsnag.notify(err instanceof Error ? err : new Error(String(err)), (event) => {
    event.context = info?.phase ? `${context}:${info.phase}` : context;
    if (metadata || info) event.addMetadata(context, { ...metadata, ...info });
  });
}
